/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import styled from "styled-components";
import type { CptCodeData } from "types/precalculatedAllowables.types";

interface IOutlierCalculation {
  inpatientRulesetId?: string;
  type?: string;
  costOutlierMethod?: string;
  costToChargeRatio: number;
  fixedLossThresholdInCents: number;
  outlierPercent: number;
  estimatedCostInCents: number;
  triggerAmountInCents: number;
}

interface IPassThroughCodeDetail {
  code: string;
  inpatientRulesetId: string;
  chargeMultiplier: number;
  thresholdInCents: number;
  chargeInCents: number;
  calculatedAmountInCents: number;
}

interface IPassThroughCalculation {
  type: string;
  totalPassThroughAmountInCents: number;
  codes: IPassThroughCodeDetail[];
}

interface ILosOutlierCalculation {
  pricingRule: string;
  ruleApplied: boolean;
  ruleStatus: string;
  drgBaseRateInCents: number;
  alos: number | null;
  los: number | null;
  losSource: string | null;
  threshold?: number | null;
  multiplier?: number;
  outlierDays?: number;
  perDiemInCents?: number;
  outlierAddOnInCents?: number;
  expectedAllowedInCents?: number;
  dischargeStatus?: string;
  transferPaymentInCents?: number;
  drgCode?: string;
}

interface ISpecialtyTabContentProps {
  cptCodes: CptCodeData[];
  specialty: string;
  outlierPaymentInCents?: number | null;
  outlierCalculation?: IOutlierCalculation | null;
  /** Pass-through calculation data — rendered as additional calculation lines */
  passThroughCalculation?: IPassThroughCalculation | null;
  /** Total charge amount from the claim, used for outlier display */
  chargeAmountInCents?: number | null;
  /** LOS outlier / transfer calculation data from DRG pricing */
  losOutlierCalculation?: ILosOutlierCalculation | null;
}

const Container = styled.div`
  padding: 20px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const PlanLabel = styled.strong`
  font-size: 16px;
  font-weight: 600;
`;

const PlanInfo = styled.span`
  font-size: 16px;
  color: #777;
  margin-left: 4px;
`;

const ContractedRatesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const ContractedRateItem = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    font-weight: 600;
    color: #333;
  }

  span {
    color: #777;
  }
`;

const VerticalBar = styled.span`
  color: #e0e0e0;
  font-size: 14px;
`;

const CalculationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CalculationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const CalculationLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  strong {
    font-weight: 600;
    color: #333;
  }

  span {
    color: #777;
  }
`;

const CalculationBullet = styled.span`
  color: #777;
  font-size: 14px;
`;

const CalculationAmount = styled.div`
  min-width: 120px;
  text-align: right;
  font-weight: 500;
  color: #333;
`;

const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-top: 16px;
  border-top: 2px solid #333;
  font-size: 16px;
  font-weight: 600;
`;

const SubtotalLabel = styled.div`
  color: #333;
`;

const SubtotalAmount = styled.div`
  color: #333;
  font-size: 18px;
`;

const OutlierSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
`;

const OutlierSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
`;

const OutlierDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  margin-bottom: 8px;
`;

const OutlierDetailLabel = styled.div`
  color: #777;
`;

const OutlierDetailAmount = styled.div`
  min-width: 120px;
  text-align: right;
  color: #333;
`;

/**
 * Format cents to currency string
 * @param cents - Amount in cents
 * @returns Formatted currency string (e.g., "$150.00")
 */
function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "N/A";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

/**
 * Component to display allowable amount calculation for a single specialty
 * Shows plan info, contracted rates, adjustment calculations, and subtotal
 */
export const SpecialtyTabContent: React.FC<ISpecialtyTabContentProps> =
  React.memo(
    ({
      cptCodes,
      specialty,
      outlierPaymentInCents,
      outlierCalculation,
      passThroughCalculation,
      chargeAmountInCents,
      losOutlierCalculation,
    }) => {
      // Get payer name from first provider procedure (they all have the same payer)
      const payerName = useMemo(() => {
        if (cptCodes.length === 0) return "";
        const firstProcedure = cptCodes[0]?.providerProcedures?.[0];
        return firstProcedure?.payerName || "";
      }, [cptCodes]);

      // Calculate subtotal: for each CPT code, use only the HIGHEST-priced provider
      // If multiple providers have the same specialty, we only count the most expensive one
      const subtotal = useMemo(() => {
        return cptCodes.reduce((total, cptCode) => {
          const providersForSpecialty = cptCode.providerProcedures.filter(
            (pp) => pp.provider.specialty === specialty
          );

          if (providersForSpecialty.length === 0) return total;

          // Find the provider with the highest finalAmountInCents
          // Tie-breaker: alphabetical by provider name
          const highestPricedProvider = providersForSpecialty.reduce(
            (max, pp) => {
              if (pp.finalAmountInCents > max.finalAmountInCents) {
                return pp;
              }
              if (
                pp.finalAmountInCents === max.finalAmountInCents &&
                pp.provider.name.localeCompare(max.provider.name) < 0
              ) {
                return pp;
              }
              return max;
            }
          );

          return total + highestPricedProvider.finalAmountInCents;
        }, 0);
      }, [cptCodes, specialty]);

      // Build contracted rates list: Array of {cptCode, providerName, rate}
      // Shows ALL providers for this specialty, not just the first one
      const contractedRates = useMemo(() => {
        const ratesList: Array<{
          cptCode: string;
          providerName: string;
          rate: number;
        }> = [];

        cptCodes.forEach((cptCode) => {
          cptCode.providerProcedures
            .filter((pp) => pp.provider.specialty === specialty)
            .forEach((pp) => {
              ratesList.push({
                cptCode: cptCode.code,
                providerName: pp.provider.name,
                rate: pp.priceInCents,
              });
            });
        });

        return ratesList;
      }, [cptCodes, specialty]);

      // Build calculation lines for ALL procedures from the highest-priced provider per CPT code
      // Shows both "paid at 100%" (no adjustments) and adjusted procedures
      // Sorted: 100% procedures first, then by procedureRank
      const calculationLines = useMemo(() => {
        interface ICalculationLine {
          cptCode: string;
          quantity: number;
          description: string;
          amountInCents: number;
          isFullPrice: boolean;
          procedureRank: number | null;
          priceInCents: number; // base price per unit
        }

        const lines: ICalculationLine[] = [];

        cptCodes.forEach((cptCode) => {
          const providersForSpecialty = cptCode.providerProcedures.filter(
            (pp) => pp.provider.specialty === specialty
          );

          if (providersForSpecialty.length === 0) return;

          // Find the provider with the highest finalAmountInCents (same logic as subtotal)
          // Tie-breaker: alphabetical by provider name
          const highestPricedProvider = providersForSpecialty.reduce(
            (max, pp) => {
              if (pp.finalAmountInCents > max.finalAmountInCents) {
                return pp;
              }
              if (
                pp.finalAmountInCents === max.finalAmountInCents &&
                pp.provider.name.localeCompare(max.provider.name) < 0
              ) {
                return pp;
              }
              return max;
            }
          );

          const numAdjustments = highestPricedProvider.adjustments.length;
          const fullPriceUnits = cptCode.quantity - numAdjustments;

          // Show full-price units (paid at 100%) if any exist
          // This handles both cases:
          // 1. No adjustments at all (all units at 100%)
          // 2. Some adjustments but quantity > adjustments (remaining units at 100%)
          if (fullPriceUnits > 0) {
            lines.push({
              cptCode: cptCode.code,
              quantity: fullPriceUnits,
              description: "Paid at 100%",
              amountInCents:
                highestPricedProvider.priceInCents * fullPriceUnits,
              isFullPrice: true,
              procedureRank: null,
              priceInCents: highestPricedProvider.priceInCents,
            });
          }

          // Show each adjustment (discounted units)
          highestPricedProvider.adjustments.forEach((adj) => {
            lines.push({
              cptCode: cptCode.code,
              quantity: 1, // Each adjustment represents a single unit
              description: adj.description,
              amountInCents: adj.amountInCents,
              isFullPrice: false,
              procedureRank: adj.procedureRank,
              priceInCents: highestPricedProvider.priceInCents,
            });
          });
        });

        // Sort: 100% procedures first (sorted by CPT code), then adjusted procedures by rank
        return lines.sort((a, b) => {
          // Full price items come first
          if (a.isFullPrice && !b.isFullPrice) return -1;
          if (!a.isFullPrice && b.isFullPrice) return 1;

          // Within full price items, sort by CPT code
          if (a.isFullPrice && b.isFullPrice) {
            return a.cptCode.localeCompare(b.cptCode);
          }

          // Within adjusted items, sort by procedureRank (nulls last)
          const rankA = a.procedureRank ?? Infinity;
          const rankB = b.procedureRank ?? Infinity;
          if (rankA !== rankB) return rankA - rankB;

          // If same rank, sort by CPT code
          return a.cptCode.localeCompare(b.cptCode);
        });
      }, [cptCodes, specialty]);

      if (
        cptCodes.length === 0 &&
        !passThroughCalculation?.codes?.length &&
        !losOutlierCalculation?.ruleApplied
      ) {
        return (
          <Container>
            <div>No data available for this specialty</div>
          </Container>
        );
      }

      return (
        <Container>
          {/* Plan Information Section */}
          {payerName && (
            <Section className="flex">
              <PlanLabel>Plan:</PlanLabel>
              <PlanInfo>{payerName}</PlanInfo>
            </Section>
          )}

          {/* When DRG pricing applies, hide precalc rates/lines and show DRG calculation only */}
          {losOutlierCalculation?.ruleApplied ? (
            <Section>
              <SectionTitle>Calculation:</SectionTitle>
              {(() => {
                const isTransfer =
                  losOutlierCalculation.pricingRule === "BCBST_DRG_TRANSFER";
                const isOutlier =
                  losOutlierCalculation.pricingRule ===
                    "BCBST_DRG_DAY_OUTLIER" &&
                  losOutlierCalculation.ruleStatus === "success";
                const isNoOutlier =
                  losOutlierCalculation.pricingRule ===
                    "BCBST_DRG_DAY_OUTLIER" &&
                  losOutlierCalculation.ruleStatus === "success_no_outlier";

                return (
                  <>
                    <OutlierSectionTitle>
                      {isTransfer &&
                        `Transfer Adjustment (Discharge Status: ${
                          losOutlierCalculation.dischargeStatus || "N/A"
                        })`}
                      {isOutlier && "LOS Day-Outlier Adjustment"}
                      {isNoOutlier && "DRG Pricing"}
                    </OutlierSectionTitle>

                    <OutlierDetailRow>
                      <OutlierDetailLabel>
                        DRG Base Rate
                        {losOutlierCalculation.drgCode &&
                          ` (${losOutlierCalculation.drgCode})`}
                      </OutlierDetailLabel>
                      <OutlierDetailAmount>
                        {formatCurrency(
                          losOutlierCalculation.drgBaseRateInCents
                        )}
                      </OutlierDetailAmount>
                    </OutlierDetailRow>
                    <OutlierDetailRow>
                      <OutlierDetailLabel>
                        Average Length of Stay (ALOS)
                      </OutlierDetailLabel>
                      <OutlierDetailAmount>
                        {losOutlierCalculation.alos} days
                      </OutlierDetailAmount>
                    </OutlierDetailRow>
                    <OutlierDetailRow>
                      <OutlierDetailLabel>
                        Actual Length of Stay
                      </OutlierDetailLabel>
                      <OutlierDetailAmount>
                        {losOutlierCalculation.los} days
                      </OutlierDetailAmount>
                    </OutlierDetailRow>

                    {/* Transfer-specific rows */}
                    {isTransfer && (
                      <>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Per Diem (
                            {formatCurrency(
                              losOutlierCalculation.drgBaseRateInCents
                            )}{" "}
                            / {losOutlierCalculation.alos})
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Transfer Payment (
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}{" "}
                            × {(losOutlierCalculation.los ?? 0) + 1} days)
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              losOutlierCalculation.transferPaymentInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                      </>
                    )}

                    {/* LOS Outlier-specific rows */}
                    {isOutlier && (
                      <>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Threshold
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {losOutlierCalculation.threshold} days
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Days ({losOutlierCalculation.los} −{" "}
                            {losOutlierCalculation.threshold})
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {losOutlierCalculation.outlierDays} days
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Per Diem (
                            {(losOutlierCalculation.multiplier ?? 0.7) * 100}% ×{" "}
                            {formatCurrency(
                              losOutlierCalculation.drgBaseRateInCents
                            )}{" "}
                            / {losOutlierCalculation.alos})
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Add-On (
                            {formatCurrency(
                              losOutlierCalculation.perDiemInCents ?? 0
                            )}{" "}
                            × {losOutlierCalculation.outlierDays})
                          </OutlierDetailLabel>
                          <CalculationAmount
                            style={{ color: "#92400e", fontWeight: 600 }}
                          >
                            +
                            {formatCurrency(
                              losOutlierCalculation.outlierAddOnInCents ?? 0
                            )}
                          </CalculationAmount>
                        </OutlierDetailRow>
                      </>
                    )}

                    {/* No-outlier info */}
                    {isNoOutlier && (
                      <>
                        <OutlierDetailRow>
                          <OutlierDetailLabel>
                            Outlier Threshold
                          </OutlierDetailLabel>
                          <OutlierDetailAmount>
                            {losOutlierCalculation.threshold} days
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                        <OutlierDetailRow>
                          <OutlierDetailLabel style={{ color: "#166534" }}>
                            LOS within threshold — no adjustment
                          </OutlierDetailLabel>
                          <OutlierDetailAmount style={{ color: "#166534" }}>
                            {formatCurrency(
                              losOutlierCalculation.expectedAllowedInCents ?? 0
                            )}
                          </OutlierDetailAmount>
                        </OutlierDetailRow>
                      </>
                    )}
                  </>
                );
              })()}
            </Section>
          ) : (
            <>
              {/* Contracted Rates Section — only when fee schedule codes exist */}
              {contractedRates.length > 0 && (
                <Section>
                  <SectionTitle>Contracted rates:</SectionTitle>
                  <ContractedRatesList>
                    {contractedRates.map((item, idx) => (
                      <React.Fragment
                        key={`${item.cptCode}-${item.providerName}-${idx}`}
                      >
                        {idx > 0 && <VerticalBar>|</VerticalBar>}
                        <ContractedRateItem>
                          <strong>{item.cptCode}</strong>
                          <span>({item.providerName}):</span>
                          <span>{formatCurrency(item.rate)}</span>
                        </ContractedRateItem>
                      </React.Fragment>
                    ))}
                  </ContractedRatesList>
                </Section>
              )}

              {/* Calculation Section */}
              <Section>
                <SectionTitle>Calculation:</SectionTitle>
                <CalculationList>
                  {calculationLines.map((line, idx) => {
                    const getDescription = () => {
                      if (!line.isFullPrice) return line.description;
                      if (line.quantity > 1)
                        return `Paid at 100% × ${line.quantity}`;
                      return "Paid at 100%";
                    };

                    return (
                      <CalculationRow key={idx}>
                        <CalculationLeft>
                          <strong>{line.cptCode}</strong>
                          <CalculationBullet>•</CalculationBullet>
                          <span>{getDescription()}</span>
                        </CalculationLeft>
                        <CalculationAmount>
                          {formatCurrency(line.amountInCents)}
                        </CalculationAmount>
                      </CalculationRow>
                    );
                  })}

                  {/* Pass-through lines */}
                  {passThroughCalculation?.codes.map((codeDetail) => (
                    <CalculationRow key={`pt-${codeDetail.code}`}>
                      <CalculationLeft>
                        <strong>{codeDetail.code}</strong>
                        <CalculationBullet>•</CalculationBullet>
                        <span>
                          {formatCurrency(codeDetail.chargeInCents)} &times;{" "}
                          {parseFloat(codeDetail.chargeMultiplier.toFixed(4))}
                        </span>
                      </CalculationLeft>
                      <CalculationAmount>
                        {formatCurrency(codeDetail.calculatedAmountInCents)}
                      </CalculationAmount>
                    </CalculationRow>
                  ))}
                </CalculationList>

                {/* High-Cost Outlier Calculation */}
                {outlierCalculation &&
                  outlierPaymentInCents != null &&
                  outlierPaymentInCents > 0 && (
                    <OutlierSection>
                      <OutlierSectionTitle>
                        High-Cost Outlier Calculation
                        {outlierCalculation.type === "FIXED_COST" &&
                          " (Fixed Cost)"}
                        {!outlierCalculation.type &&
                          outlierCalculation.costOutlierMethod ===
                            "FIXED_STOP_LOSS" &&
                          " (Fixed Stop-Loss)"}
                      </OutlierSectionTitle>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Estimated Cost (
                          {formatCurrency(
                            Math.round(
                              outlierCalculation.estimatedCostInCents /
                                outlierCalculation.costToChargeRatio
                            )
                          )}{" "}
                          ×{" "}
                          {parseFloat(
                            outlierCalculation.costToChargeRatio.toFixed(4)
                          )}{" "}
                          CCR)
                        </OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            outlierCalculation.estimatedCostInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      <OutlierDetailRow>
                        <OutlierDetailLabel>
                          Trigger (
                          {formatCurrency(
                            outlierCalculation.triggerAmountInCents -
                              outlierCalculation.fixedLossThresholdInCents
                          )}{" "}
                          +{" "}
                          {formatCurrency(
                            outlierCalculation.fixedLossThresholdInCents
                          )}{" "}
                          threshold)
                        </OutlierDetailLabel>
                        <OutlierDetailAmount>
                          {formatCurrency(
                            outlierCalculation.triggerAmountInCents
                          )}
                        </OutlierDetailAmount>
                      </OutlierDetailRow>
                      {outlierPaymentInCents != null &&
                        outlierPaymentInCents > 0 && (
                          <>
                            <OutlierDetailRow>
                              <OutlierDetailLabel>
                                Excess (
                                {formatCurrency(
                                  outlierCalculation.estimatedCostInCents
                                )}{" "}
                                −{" "}
                                {formatCurrency(
                                  outlierCalculation.triggerAmountInCents
                                )}
                                )
                              </OutlierDetailLabel>
                              <OutlierDetailAmount>
                                {formatCurrency(
                                  outlierCalculation.estimatedCostInCents -
                                    outlierCalculation.triggerAmountInCents
                                )}
                              </OutlierDetailAmount>
                            </OutlierDetailRow>
                            <OutlierDetailRow>
                              <OutlierDetailLabel>
                                Outlier Payment (
                                {formatCurrency(
                                  outlierCalculation.estimatedCostInCents -
                                    outlierCalculation.triggerAmountInCents
                                )}{" "}
                                × {outlierCalculation.outlierPercent}%)
                              </OutlierDetailLabel>
                              <CalculationAmount
                                style={{ color: "#92400e", fontWeight: 600 }}
                              >
                                +{formatCurrency(outlierPaymentInCents)}
                              </CalculationAmount>
                            </OutlierDetailRow>
                          </>
                        )}
                    </OutlierSection>
                  )}
              </Section>
            </>
          )}

          {/* Subtotal */}
          <SubtotalRow>
            <SubtotalLabel>Subtotal:</SubtotalLabel>
            <SubtotalAmount>
              {formatCurrency(
                losOutlierCalculation?.ruleApplied
                  ? (losOutlierCalculation.expectedAllowedInCents ?? 0) +
                      (outlierPaymentInCents ?? 0)
                  : subtotal +
                      (passThroughCalculation?.totalPassThroughAmountInCents ??
                        0) +
                      (outlierPaymentInCents ?? 0)
              )}
            </SubtotalAmount>
          </SubtotalRow>
        </Container>
      );
    }
  );
